/**
 * useCheckout Hook
 * 
 * Manages checkout state and handles the checkout process.
 * 
 * @module hooks/useCheckout
 * 
 * Checkout Flow:
 * 1. Calculate cart total
 * 2. If paid items: Show Stripe checkout form
 * 3. If free samples: Process directly with inventory check
 * 4. Record order to database
 * 5. Send confirmation email
 * 
 * Usage:
 * ```ts
 * const {
 *   showCheckoutForm,
 *   setShowCheckoutForm,
 *   checkoutData,
 *   setCheckoutData,
 *   handleCartCheckout
 * } = useCheckout({
 *   cartItems, setCartItems, setShowCart, setToastMessage, language
 * });
 * ```
 */
import { useState, useCallback, Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { auth } from '../services/firebaseConfig';
import { reserveInventory, checkAvailability } from '../services/inventoryService';
import { authFetch } from '../services/apiAuth';
import { ensureFreshAuth, recordAuthTimestamp } from '../services/reauthService';
import type { CartItem } from '../types';

// ==================== Types ====================

/**
 * Cart item with extended product details
 * Uses the shared CartItem type for compatibility
 */
export type CheckoutCartItem = CartItem;

/**
 * Extended unavailable item with display info
 * Runtime data may include more properties than base CartItem
 */
interface UnavailableItemDisplay {
  id: string;
  name?: string;
  quantity: number;
  requested?: number;
  available?: number;
}

/**
 * Extended availability result with display-friendly data
 */
interface AvailabilityResult {
  success?: boolean;
  available: boolean;
  unavailableItems: UnavailableItemDisplay[];
}

/**
 * Extended reserve result with reservation details
 */
interface ExtendedReserveResult {
  success: boolean;
  message?: string;
  error?: string;
  reservations?: Array<{
    productId: string;
    quantityReserved: number;
  }>;
}

/**
 * Checkout information from shipping form
 */
export interface CheckoutInfo {
  name: string;
  email?: string;
  phone?: string;
  address: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  deliveryRequest?: string;
}

/**
 * Discount information
 */
export interface DiscountInfo {
  code: string;
  percentage: number;
}

/**
 * Checkout data passed to payment form
 */
export interface CheckoutData {
  items: CheckoutCartItem[];
  subtotal: number;
  discountAmount: number;
  total: number;
  discount: DiscountInfo | null;
}

/**
 * Order result from API
 */
interface OrderResult {
  success: boolean;
  order?: {
    orderId: string;
    [key: string]: unknown;
  };
  error?: string;
}

/**
 * Email result from API
 */
interface EmailResult {
  success: boolean;
  error?: string;
}

/**
 * Options for useCheckout hook
 */
export interface UseCheckoutOptions {
  cartItems: CheckoutCartItem[];
  setCartItems: Dispatch<SetStateAction<CheckoutCartItem[]>>;
  setShowCart: (show: boolean) => void;
  setToastMessage: Dispatch<SetStateAction<string | null>>;
  language: string;
  /** Optional: Clear cart function that also syncs to Firebase */
  clearCart?: () => void;
}

/**
 * Return type for useCheckout hook
 */
export interface UseCheckoutReturn {
  showCheckoutForm: boolean;
  setShowCheckoutForm: Dispatch<SetStateAction<boolean>>;
  checkoutData: CheckoutData | null;
  setCheckoutData: Dispatch<SetStateAction<CheckoutData | null>>;
  handleCartCheckout: (checkoutInfo: CheckoutInfo) => Promise<void>;
}

// ==================== Hook Implementation ====================

/**
 * Hook for managing checkout state and process
 * 
 * @param options - Configuration options
 * @returns Checkout state and handlers
 */
export function useCheckout({
  cartItems,
  setCartItems,
  setShowCart,
  setToastMessage,
  language,
  clearCart,
}: UseCheckoutOptions): UseCheckoutReturn {
  const { t } = useTranslation(['checkout']);
  const [showCheckoutForm, setShowCheckoutForm] = useState<boolean>(false);
  const [checkoutData, setCheckoutData] = useState<CheckoutData | null>(null);

  /**
   * Handle cart checkout process
   * Routes to Stripe for paid orders, processes directly for free samples
   * Requires re-authentication if session is older than 15 minutes
   * 
   * @param checkoutInfo - Shipping/contact information
   */
  const handleCartCheckout = useCallback(async (checkoutInfo: CheckoutInfo): Promise<void> => {
    try {
      // 🔐 Re-authentication check for payment security
      // Require fresh auth (within 15 min) before processing checkout
      const reauthResult = await ensureFreshAuth(auth.currentUser, 15 * 60 * 1000);
      
      if (!reauthResult.success) {
        console.warn('⛔ Checkout blocked: re-authentication required');
        setToastMessage(`🔐 ${reauthResult.error || t('errors.reauthRequired')}`);
        return;
      }

      // Calculate total to determine if this is paid or free
      const subtotal = cartItems.reduce((sum, item) => {
        const price = item.priceUSD || parseFloat(String(item.price)) || 0;
        return sum + (price * item.quantity);
      }, 0);

      // If there are paid items, use Stripe checkout
      if (subtotal > 0) {
        console.log('💳 Paid order detected, routing to Stripe checkout');
        setCheckoutData({
          items: cartItems,
          subtotal,
          discountAmount: 0,
          total: subtotal,
          discount: null
        });
        setShowCart(false);
        setShowCheckoutForm(true);
        return;
      }

      // Free samples flow (existing logic)
      console.log('🎁 Free sample order, processing directly');
      
      // Check inventory availability
      // Cast to extended type since runtime data includes more properties
      const availability = await checkAvailability(
        cartItems.map(item => ({ id: item.id, quantity: item.quantity }))
      ) as unknown as AvailabilityResult;
      
      if (!availability.available) {
        const unavailableNames = availability.unavailableItems
          .map(item => {
            const name = item.name || item.id;
            const requested = item.requested || item.quantity;
            const available = item.available ?? 0;
            return `${name} (${t('inventory.need')}: ${requested}, ${t('inventory.available')}: ${available})`;
          })
          .join(', ');
        
        setToastMessage(`⚠️ ${t('inventory.outOfStock')}: ${unavailableNames}`);
        return;
      }
      
      // Reserve inventory
      // Cast to extended type since runtime data includes reservations
      const reserveResult = await reserveInventory(
        cartItems.map(item => ({ id: item.id, quantity: item.quantity }))
      ) as unknown as ExtendedReserveResult;
      
      if (!reserveResult.success) {
        setToastMessage(`❌ ${reserveResult.error || reserveResult.message || t('errors.reservationFailed')}`);
        return;
      }
      
      // Record order to database
      try {
        const orderResponse = await authFetch('/api/recordOrder', {
          method: 'POST',
          body: JSON.stringify({
            orderId: `ORD-${Date.now()}`,
            userId: auth.currentUser?.uid || 'guest',
            items: cartItems,
            subtotal: cartItems.reduce((sum, item) => sum + ((item.priceUSD || parseFloat(String(item.price)) || 0) * item.quantity), 0),
            discount: 0,
            total: cartItems.reduce((sum, item) => sum + ((item.priceUSD || parseFloat(String(item.price)) || 0) * item.quantity), 0),
            shippingInfo: checkoutInfo,
            discountCode: null
          })
        });
        
        const orderResult: OrderResult = await orderResponse.json();
        
        if (orderResult.success && orderResult.order) {
          // Send order confirmation email
          try {
            const emailResponse = await authFetch('/api/sendOrderConfirmation', {
              method: 'POST',
              body: JSON.stringify({
                email: checkoutInfo.email || auth.currentUser?.email,
                orderId: orderResult.order.orderId,
                items: cartItems.map(item => ({
                  name: item.name,
                  quantity: item.quantity,
                  priceUSD: item.priceUSD || parseFloat(String(item.price)) || 0
                })),
                total: cartItems.reduce((sum, item) => sum + ((item.priceUSD || parseFloat(String(item.price)) || 0) * item.quantity), 0),
                shippingInfo: {
                  name: checkoutInfo.name,
                  address1: checkoutInfo.address,
                  address2: checkoutInfo.deliveryRequest || '',
                  city: checkoutInfo.city || 'Seoul',
                  state: checkoutInfo.state || '',
                  postalCode: checkoutInfo.postalCode || '',
                  country: checkoutInfo.country || 'Korea'
                }
              })
            });

            const emailResult: EmailResult = await emailResponse.json();
            
            if (emailResult.success) {
              console.log('📧 Order confirmation email sent to:', checkoutInfo.email || auth.currentUser?.email);
            } else {
              console.warn('⚠️ Email sending failed:', emailResult.error);
            }
          } catch (emailError) {
            console.error('Email error:', emailError);
            // Don't block checkout if email fails
          }

          // Clear cart - use clearCart if provided (syncs to Firebase), otherwise setCartItems
          if (clearCart) {
            clearCart();
          } else {
            setCartItems([] as CheckoutCartItem[]);
          }
          setShowCart(false);
          
          // Calculate total items from reservations or cart
          const totalItems = reserveResult.reservations
            ? reserveResult.reservations.reduce((sum, r) => sum + r.quantityReserved, 0)
            : cartItems.reduce((sum, item) => sum + item.quantity, 0);
          
          setToastMessage(language === 'en'
            ? `✅ Order #${orderResult.order.orderId} confirmed! ${totalItems} items.`
            : `✅ 주문 #${orderResult.order.orderId} 완료! ${totalItems}개 상품.`
          );
          
          console.log('✅ Order completed:', {
            order: orderResult.order,
            reservations: reserveResult.reservations
          });
        } else {
          throw new Error('Order recording failed');
        }
      } catch (orderError) {
        console.error('Order recording error:', orderError);
        // Inventory was reserved but order failed - in production, implement rollback
        setToastMessage(language === 'en'
          ? '⚠️ Order saved but confirmation failed. Contact support.'
          : '⚠️ 주문은 저장되었지만 확인 실패. 고객센터에 문의하세요.'
        );
      }
      
    } catch (error) {
      console.error('Checkout error:', error);
      setToastMessage(language === 'en'
        ? '❌ Checkout failed. Please try again.'
        : '❌ 결제 실패. 다시 시도해주세요.'
      );
    }
  }, [cartItems, language, setCartItems, setShowCart, setToastMessage]);

  return {
    showCheckoutForm,
    setShowCheckoutForm,
    checkoutData,
    setCheckoutData,
    handleCartCheckout
  };
}

export default useCheckout;
